import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useArtistAuth } from '@/context/ArtistAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const PMB_EMOJIS = ['??', '??', '??', '??', '??', '??', '??', '??', '??', '?'];
const asDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const parsed = asDate(value);
  if (!parsed) return 'Unknown time';
  return parsed.toLocaleString();
};

const toDayKey = (value) => {
  const parsed = asDate(value);
  if (!parsed) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDayLabel = (dayKey) => {
  if (!dayKey) return 'Unknown day';
  const parsed = asDate(`${dayKey}T00:00:00`);
  if (!parsed) return dayKey;
  return parsed.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

const conversationMessages = (conversation) => (Array.isArray(conversation?.messages) ? conversation.messages : []);

const latestMessage = (conversation) => {
  const messages = conversationMessages(conversation);
  return messages.length ? messages[messages.length - 1] : null;
};

const unreadCount = (conversation) => conversationMessages(conversation).filter((entry) => !entry?.read).length;

const artistConversationKey = (conversation) => {
  if (!conversation) return '';
  if (conversation.collaborator_id === 'admin' || conversation.status === 'admin_note') return 'admin_note';
  return String(conversation.collaborator_id || conversation.collaborator_name || conversation.id || '').trim().toLowerCase();
};

const ArtistInbox = () => {
  const { artist } = useArtistAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [conversationActionBusyId, setConversationActionBusyId] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  const [sendWarning, setSendWarning] = useState('');

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/artist-auth/enquiries`, { withCredentials: true });
      const items = response.data.conversations || [];
      setConversations(items);
      setActiveConversationId((prev) => {
        if (prev && items.some((item) => item.id === prev)) return prev;
        return items[0]?.id || '';
      });
      setLastRefreshedAt(new Date().toISOString());
    } finally {
      if (!silent) setLoading(false);
      if (silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initialLoad = async () => {
      if (!mounted) return;
      await loadConversations();
    };

    initialLoad();
    return () => {
      mounted = false;
    };
  }, [loadConversations]);

  useEffect(() => {
    const handleRefreshRequest = () => {
      loadConversations({ silent: true });
    };
    window.addEventListener('kalamanch:pmb-refresh-request', handleRefreshRequest);
    return () => {
      window.removeEventListener('kalamanch:pmb-refresh-request', handleRefreshRequest);
    };
  }, [loadConversations]);

  const activeConversation = conversations.find((item) => item.id === activeConversationId);

  const filteredConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((conversation) => {
      const sender = getThreadSenderName(conversation).toLowerCase();
      const subject = String(conversation.subject || '').toLowerCase();
      const preview = String(latestMessage(conversation)?.message || '').toLowerCase();
      return sender.includes(query) || subject.includes(query) || preview.includes(query);
    });
  }, [conversations, searchQuery]);

  const relatedConversations = useMemo(() => {
    if (!activeConversation) return [];
    const key = artistConversationKey(activeConversation);
    return conversations.filter((item) => artistConversationKey(item) === key);
  }, [activeConversation, conversations]);

  const mergedMessages = useMemo(
    () =>
      relatedConversations
        .flatMap((conversation) => conversationMessages(conversation))
        .filter((entry, index, all) => all.findIndex((candidate) => candidate.id === entry.id) === index)
        .sort((a, b) => (asDate(a.created_at)?.getTime() || 0) - (asDate(b.created_at)?.getTime() || 0)),
    [relatedConversations]
  );

  const dayKeys = useMemo(
    () => Array.from(new Set(mergedMessages.map((entry) => toDayKey(entry.created_at)).filter(Boolean))).sort().reverse(),
    [mergedMessages]
  );

  const visibleMessages = selectedDay
    ? mergedMessages.filter((entry) => toDayKey(entry.created_at) === selectedDay)
    : mergedMessages;

  const chatRenderItems = useMemo(() => {
    const items = [];
    let lastDayKey = '';
    for (const entry of visibleMessages) {
      const dayKey = toDayKey(entry.created_at);
      if (dayKey && dayKey !== lastDayKey) {
        items.push({ type: 'day', id: `day_${dayKey}`, dayKey });
        lastDayKey = dayKey;
      }
      items.push({ type: 'message', id: entry.id, entry });
    }
    return items;
  }, [visibleMessages]);

  useEffect(() => {
    if (!activeConversation) {
      setSelectedDay('');
      return;
    }
    const days = Array.from(new Set(mergedMessages.map((entry) => toDayKey(entry.created_at)).filter(Boolean))).sort().reverse();
    setSelectedDay((prev) => {
      if (prev === '') return '';
      if (prev && days.includes(prev)) return prev;
      return days[0] || '';
    });
  }, [activeConversation, mergedMessages]);

  function getThreadSenderName(conversation) {
    return conversation?.collaborator_id === 'admin' || conversation?.status === 'admin_note'
      ? 'Kala Manch Admin'
      : conversation?.collaborator_name;
  }

  const getThreadStatusLabel = (conversation) =>
    conversation?.collaborator_id === 'admin' || conversation?.status === 'admin_note'
      ? 'Profile review note'
      : conversation?.status;

  const handleSend = async () => {
    if (!message.trim() || !activeConversationId) return;
    setSending(true);
    setSendWarning('');
    try {
      const response = await axios.post(
        `${API}/artist-auth/enquiries/${activeConversationId}/messages`,
        { message },
        { withCredentials: true }
      );
      setConversations((prev) =>
        prev.map((conversation) =>
          conversation.id === activeConversationId
            ? {
                ...conversation,
                messages: [...conversationMessages(conversation), response.data.message],
                updated_at: response.data.message.created_at,
              }
            : conversation
        )
      );
      setMessage('');
    } catch (error) {
      setSendWarning(error.response?.data?.detail || 'Message could not be sent. Please turn off VPN/proxy if enabled and keep PMB conversations professional and lawful.');
    } finally {
      setSending(false);
    }
  };

  const patchConversationMessages = (conversationId, updater) => {
    setConversations((prev) =>
      prev.map((conversation) => {
        if (conversation.id !== conversationId) return conversation;
        return { ...conversation, messages: updater(conversationMessages(conversation)) };
      })
    );
  };

  const handleConversationReadState = async (conversation, nextRead) => {
    const messages = conversationMessages(conversation);
    if (!conversation?.id || !messages.length) return;
    setConversationActionBusyId(conversation.id);
    try {
      for (const entry of messages) {
        if (!entry?.id || Boolean(entry.read) === Boolean(nextRead)) continue;
        await axios.patch(
          `${API}/artist-auth/enquiries/${conversation.id}/messages/${entry.id}`,
          { read: Boolean(nextRead) },
          { withCredentials: true }
        );
      }
      patchConversationMessages(conversation.id, (current) =>
        current.map((entry) => ({ ...entry, read: Boolean(nextRead) }))
      );
    } finally {
      setConversationActionBusyId('');
    }
  };

  const handleDeleteConversationMessages = async (conversation) => {
    if (!conversation?.id) return;
    if (!window.confirm('Delete all messages from this conversation row?')) return;
    setConversationActionBusyId(conversation.id);
    try {
      await axios.delete(`${API}/artist-auth/enquiries/${conversation.id}`, { withCredentials: true });
      setConversations((prev) => prev.filter((item) => item.id !== conversation.id));
      setActiveConversationId((prev) => (prev === conversation.id ? '' : prev));
    } finally {
      setConversationActionBusyId('');
    }
  };

  const toggleConversationSelection = (conversationId, checked) => {
    setSelectedConversationIds((prev) =>
      checked ? Array.from(new Set([...prev, conversationId])) : prev.filter((id) => id !== conversationId)
    );
  };

  const toggleSelectAll = () => {
    if (!filteredConversations.length) return;
    if (selectedConversationIds.length === filteredConversations.length) {
      setSelectedConversationIds([]);
      return;
    }
    setSelectedConversationIds(filteredConversations.map((conversation) => conversation.id));
  };

  const handleBulkReadState = async (nextRead) => {
    const selected = conversations.filter((conversation) => selectedConversationIds.includes(conversation.id));
    for (const conversation of selected) {
      await handleConversationReadState(conversation, nextRead);
    }
  };

  const handleBulkDelete = async () => {
    const selected = conversations.filter((conversation) => selectedConversationIds.includes(conversation.id));
    for (const conversation of selected) {
      await handleDeleteConversationMessages(conversation);
    }
    setSelectedConversationIds([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-[2rem] shadow-lg border border-orange-100 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] min-h-[72vh]">
            <aside className="border-r border-orange-100 bg-orange-50/40">
              <div className="p-6 border-b border-orange-100 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm uppercase tracking-[0.25em] text-[#FF6F00] font-semibold">Artist PMB</p>
                  <button
                    type="button"
                    onClick={() => loadConversations({ silent: true })}
                    className="rounded-full border border-orange-200 px-3 py-1 text-[11px] font-semibold text-orange-700 hover:bg-orange-100"
                  >
                    Refresh
                  </button>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">{artist?.name || 'Artist Inbox'}</h1>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="rounded-full border border-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
                  >
                    {selectedConversationIds.length === filteredConversations.length && filteredConversations.length > 0
                      ? 'Clear'
                      : 'Select all'}
                  </button>
                  {selectedConversationIds.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleBulkReadState(true)}
                        className="rounded-full border border-green-200 px-3 py-1 text-[11px] font-semibold text-green-700 hover:bg-green-50"
                      >
                        Read all
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulkReadState(false)}
                        className="rounded-full border border-sky-200 px-3 py-1 text-[11px] font-semibold text-sky-700 hover:bg-sky-50"
                      >
                        Unread
                      </button>
                      <button
                        type="button"
                        onClick={handleBulkDelete}
                        className="rounded-full border border-red-200 px-3 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                      >
                        Delete all
                      </button>
                    </>
                  )}
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search PMB..."
                  className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400"
                />
              </div>
              <div className="p-4 space-y-3">
                {loading ? (
                  <p className="text-gray-500">Loading enquiries...</p>
                ) : filteredConversations.length === 0 ? (
                  <p className="text-gray-500">No enquiries found.</p>
                ) : (
                  filteredConversations.map((conversation) => {
                    const isActive = conversation.id === activeConversationId;
                    const busy = conversationActionBusyId === conversation.id;
                    const unread = unreadCount(conversation);
                    return (
                      <div
                        key={conversation.id}
                        className={`rounded-2xl px-4 py-4 transition ${
                          isActive ? 'bg-white shadow-md border border-orange-100' : 'hover:bg-white'
                        }`}
                      >
                        <button className="w-full text-left" onClick={() => setActiveConversationId(conversation.id)}>
                          <div className="flex items-center justify-between">
                            <input
                              type="checkbox"
                              checked={selectedConversationIds.includes(conversation.id)}
                              onChange={(event) => toggleConversationSelection(conversation.id, event.target.checked)}
                              className="h-4 w-4 accent-orange-600"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-semibold text-gray-900">{getThreadSenderName(conversation)}</p>
                            {unread > 0 && (
                              <span className="rounded-full bg-orange-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                {unread}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {latestMessage(conversation)?.message || conversation.subject || 'No message yet'}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            {formatDateTime(latestMessage(conversation)?.created_at || conversation.updated_at || conversation.created_at)}
                          </p>
                        </button>
                        {isActive && (
                          <div className="mt-3 flex items-center gap-3 text-[11px]">
                            <button
                              type="button"
                              onClick={() => handleConversationReadState(conversation, true)}
                              disabled={busy}
                              className="underline underline-offset-2 disabled:opacity-60"
                            >
                              Mark read
                            </button>
                            <button
                              type="button"
                              onClick={() => handleConversationReadState(conversation, false)}
                              disabled={busy}
                              className="underline underline-offset-2 disabled:opacity-60"
                            >
                              Mark unread
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteConversationMessages(conversation)}
                              disabled={busy}
                              className="underline underline-offset-2 text-red-700 disabled:opacity-60"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </aside>
            <section className="p-6 lg:p-8 flex flex-col">
              {!activeConversation ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">Choose a conversation to reply to.</div>
              ) : (
                <>
                  <div className="sticky top-24 z-20 -mt-1 mb-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => loadConversations({ silent: true })}
                      className="rounded-full border border-orange-200 bg-white/95 px-3 py-1 text-[11px] font-semibold text-orange-700 shadow-sm backdrop-blur hover:bg-orange-100"
                    >
                      Refresh Thread
                    </button>
                  </div>
                  <div className="pb-4 border-b border-orange-100">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{getThreadSenderName(activeConversation)}</h2>
                      <p className="text-sm text-gray-500 mt-1">{activeConversation.subject}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {`${refreshing ? 'Refreshing...' : 'Manual refresh'} | Last sync: ${formatDateTime(lastRefreshedAt)}`}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          !selectedDay ? 'border-[#FF6F00] bg-orange-50 text-[#B45309]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedDay('')}
                      >
                        All days
                      </button>
                      {dayKeys.map((dayKey) => (
                        <button
                          key={dayKey}
                          type="button"
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                            selectedDay === dayKey ? 'border-[#FF6F00] bg-orange-50 text-[#B45309]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedDay(dayKey)}
                        >
                          {formatDayLabel(dayKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto py-6 space-y-4 pr-1">
                    {chatRenderItems.length === 0 ? (
                      <p className="text-sm text-gray-500">No messages for the selected day.</p>
                    ) : (
                      chatRenderItems.map((item) =>
                        item.type === 'day' ? (
                          <div key={item.id} className="flex items-center gap-3 py-1">
                            <div className="h-px flex-1 bg-orange-100" />
                            <p className="text-[11px] font-semibold text-orange-700">{formatDayLabel(item.dayKey)}</p>
                            <div className="h-px flex-1 bg-orange-100" />
                          </div>
                        ) : (
                          <div
                            key={item.id}
                            className={`max-w-2xl rounded-2xl px-4 py-3 shadow-sm ${
                              item.entry.sender_type === 'artist' ? 'ml-auto bg-[#FF6F00] text-white' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <p className="text-xs font-semibold uppercase tracking-wide mb-1 opacity-80">{item.entry.sender_name}</p>
                            <p className="leading-relaxed">{item.entry.message}</p>
                            <p className="text-[11px] mt-2 opacity-75">{formatDateTime(item.entry.created_at)}</p>
                          </div>
                        )
                      )
                    )}
                  </div>
                  <div className="pt-4 border-t border-orange-100 flex gap-3">
                    <div className="flex-1 space-y-2">
                      {showEmojiBar && (
                        <div className="flex flex-wrap gap-2 rounded-xl border border-orange-100 bg-orange-50/70 p-2">
                          {PMB_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => setMessage((prev) => `${prev}${emoji}`)}
                              className="rounded-full border border-orange-200 bg-white px-2 py-1 text-sm hover:bg-orange-100"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                      <textarea
                        value={message}
                        onChange={(event) => {
                          setMessage(event.target.value);
                          if (sendWarning) setSendWarning('');
                        }}
                        rows="3"
                        placeholder="Write your reply..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-2xl"
                      />
                      {sendWarning && (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                          {sendWarning}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 self-end">
                      <button
                        type="button"
                        onClick={() => setShowEmojiBar((prev) => !prev)}
                        className="rounded-full border border-orange-200 px-3 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                      >
                        Emoji
                      </button>
                      <button type="button" onClick={handleSend} disabled={sending || !message.trim()} className="btn-primary">
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistInbox;
